import { Condition } from '../../condition';

export class ThEachConditionsBuilder {
  private readonly _name: string;
  private readonly _conditions: Condition[];

  constructor(name: string, conditions: Condition[]) {
    this._name = name;
    this._conditions = conditions;
  }

  get value(): string {
    const expressions: string[] = [];

    // we always want to check if the item is not null first
    expressions.push(`(${this._name} neq null)`);

    this._conditions.forEach((condition) => {
      let expr;
      if (this.isIncludesExpression(condition)) {
        expr = this.includesExpression(condition);
      } else {
        expr = this.standardExpression(condition);
      }

      if (this.needsNullCheck(condition.operator)) {
        expr = `(${this.makeFieldName(condition.fieldPath)} neq null) and (${expr})`;
      }

      expressions.push(`(${expr})`);
    });

    return `${expressions.join(' and ')}`;
  }

  toString(): string {
    return `ConditionsExpr="${this.value}"`;
  }

  private makeFieldName(fieldPath: string): string {
    const fieldName = fieldPath.split('.').slice(-1)[0];
    return `${this._name}.${this.escapeDoubleUnderscores(fieldName)}`;
  }

  private encodeFieldName(fieldName: string, fieldType: string): string {
    if (fieldType === 'date') {
      return `#strings.substring(${fieldName},0,10)`;
    }

    return fieldName;
  }

  private encodeOperator(op: string): string {
    const opMap: Record<string, string> = {
      ['>']: 'gt',
      ['<']: 'lt',
      ['>=']: 'gte',
      ['<=']: 'lte',
      ['!=']: 'neq',
      ['=']: 'eq',
    };
    return opMap[op] || op;
  }

  private encodeConditionValue(condition: Condition): string | number | boolean {
    // Always do this check first
    if (Array.isArray(condition.value)) {
      return `{'${condition.value.map((c) => c.label).join("','")}'}`;
    }

    if (condition.fieldType === 'boolean') {
      return condition.value as boolean;
    }

    if (condition.fieldType === 'number' || condition.fieldType === 'integer' || condition.fieldType === 'double') {
      return condition.value as number;
    }

    // string and date field types
    return `'${condition.value}'`;
  }

  private standardExpression(condition: Condition): string {
    const encodedFieldName = this.encodeFieldName(this.makeFieldName(condition.fieldPath), condition.fieldType);
    const encodedOperator = this.encodeOperator(condition.operator);
    const encodedValue = this.encodeConditionValue(condition);
    return `${encodedFieldName} ${encodedOperator} ${encodedValue}`;
  }

  private isIncludesExpression(condition: Condition): boolean {
    return condition.operator === '~' || condition.operator === '!~';
  }

  private needsNullCheck(operator: string): boolean {
    return operator === '<' || operator === '>' || operator === '<=' || operator === '>=';
  }

  private includesExpression(condition: Condition): string {
    if (condition.fieldType === 'date') {
      throw new Error('Date field types not supported for Includes');
    }

    const encodedFieldName = this.encodeFieldName(this.makeFieldName(condition.fieldPath), condition.fieldType);
    const encodedValue = this.encodeConditionValue(condition);
    const invert = condition.operator === '~' ? '' : '!';

    return `${invert}#lists.includes(${encodedFieldName},${encodedValue})`;
  }

  private escapeDoubleUnderscores(fieldName: string): string {
    return fieldName.replace(/__/g, '\\_\\_');
  }
}
